import { useRef, useEffect, useCallback, useState } from "react";
import {
  DefaultSizeStyle,
  Tldraw,
  getSvgPathFromPoints,
  AssetRecordType,
  createShapeId,
} from "tldraw";

import { TopBar } from "../../layout/TopBar.jsx";
import { GenerateBar } from "../../layout/GenerateBar.jsx";
import WelcomeModal from "../../layout/WelcomeModal.jsx";
import { useAppContext } from "../../../contexts/AppContext.jsx";
import { useAuth } from "../../../contexts/AuthContext.jsx";
import {
  prepareImageBlob,
  submitGeneration,
  pollForResult,
} from "../../../services/generate";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "../../../hooks/useApi";

function LaserScribble({ scribble, zoom, color, opacity, className }) {
  if (!scribble.points.length) return null;

  return (
    <svg className={"tl-overlays__item"}>
      <path
        className="tl-scribble"
        d={getSvgPathFromPoints(scribble.points, false)}
        stroke="rgba(255, 0, 0, 0.5)"
        fill="none"
        strokeWidth={8 / zoom}
        opacity={opacity ?? scribble.opacity}
      />
    </svg>
  );
}

const PLACEHOLDER_SIZE = 300;

function GenerationLoadingOverlay({ editor, activeGenerationsRef }) {
  const [overlays, setOverlays] = useState([]);

  useEffect(() => {
    if (!editor) return;

    const update = () => {
      const result = [];
      for (const [taskId, gen] of activeGenerationsRef.current.entries()) {
        const shape = editor.getShape(gen.placeholderShapeId);
        if (!shape) continue;

        // Convert page coords to screen coords
        const topLeft = editor.pageToViewport({ x: shape.x, y: shape.y });
        const bottomRight = editor.pageToViewport({
          x: shape.x + PLACEHOLDER_SIZE,
          y: shape.y + PLACEHOLDER_SIZE,
        });

        result.push({
          taskId,
          x: topLeft.x,
          y: topLeft.y,
          w: bottomRight.x - topLeft.x,
          h: bottomRight.y - topLeft.y,
        });
      }
      setOverlays(result);
    };

    // Update on camera/shape changes
    const unsub = editor.store.listen(update, { scope: "all" });
    update();
    return unsub;
  }, [editor, activeGenerationsRef]);

  if (overlays.length === 0) return null;

  return (
    <>
      {overlays.map((o) => (
        <div
          key={o.taskId}
          style={{
            position: "absolute",
            left: o.x,
            top: o.y,
            width: o.w,
            height: o.h,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 999,
          }}
        >
          <div className="generation-spinner" />
          <span
            style={{
              marginTop: 10,
              fontSize: 13,
              fontWeight: 500,
              color: "#666",
              fontFamily: "'Inter', sans-serif",
            }}
          >
            Generating...
          </span>
        </div>
      ))}
    </>
  );
}

export default function CanvasComponent() {
  const { editor, setEditor } = useAppContext();
  const { refreshUser } = useAuth();
  const queryClient = useQueryClient();

  // Track active generations: taskId -> { placeholderShapeId, intervalId }
  const activeGenerationsRef = useRef(new Map());
  const [, setGenerationCount] = useState(0);
  const bumpGenerations = useCallback(() => setGenerationCount((c) => c + 1), []);

  // Cleanup all polling intervals on unmount
  useEffect(() => {
    return () => {
      for (const gen of activeGenerationsRef.current.values()) {
        clearInterval(gen.intervalId);
      }
      activeGenerationsRef.current.clear();
    };
  }, []);

  const handleGenerate = useCallback(
    async (editor, prompt, style, modelTier) => {
      try {
        const selectedShapes = editor.getSelectedShapes();
        if (selectedShapes.length === 0) {
          alert("Please select the shapes you want to use for generation");
          return;
        }

        const selectionBounds = editor.getSelectionPageBounds();
        if (!selectionBounds) {
          alert("Could not determine selection bounds");
          return;
        }

        // 1. Prepare the image blob from selection
        const blob = await prepareImageBlob(editor);

        // 2. Submit to backend
        const { id: taskId } = await submitGeneration(
          blob,
          prompt,
          style,
          modelTier
        );

        // 3. Create placeholder shape next to selection
        const placeholderShapeId = createShapeId();
        editor.createShape({
          id: placeholderShapeId,
          type: "geo",
          x: selectionBounds.maxX + 20,
          y: selectionBounds.y,
          opacity: 0.5,
          props: {
            w: PLACEHOLDER_SIZE,
            h: PLACEHOLDER_SIZE,
            geo: "rectangle",
            fill: "solid",
            color: "grey",
          },
          meta: {
            generationId: taskId,
            status: "pending",
          },
        });

        // 4. Start polling
        const intervalId = pollForResult(taskId, {
          onComplete: (dataUrl) => {
            activeGenerationsRef.current.delete(taskId);
            bumpGenerations();

            // Get placeholder position before deleting
            const placeholder = editor.getShape(placeholderShapeId);
            if (!placeholder) return;

            const pos = { x: placeholder.x, y: placeholder.y };

            // Load image to get dimensions
            const img = new Image();
            img.onload = () => {
              let w = img.width;
              let h = img.height;

              // Scale down to max 500px
              const maxSize = 500;
              if (w > maxSize || h > maxSize) {
                if (w > h) {
                  h = (h / w) * maxSize;
                  w = maxSize;
                } else {
                  w = (w / h) * maxSize;
                  h = maxSize;
                }
              }

              // Delete placeholder
              editor.deleteShape(placeholderShapeId);

              // Create image asset + shape
              const assetId = AssetRecordType.createId();
              editor.createAssets([
                {
                  id: assetId,
                  type: "image",
                  typeName: "asset",
                  props: {
                    name: `generation-${taskId}.png`,
                    src: dataUrl,
                    w: img.width,
                    h: img.height,
                    mimeType: "image/png",
                    isAnimated: false,
                  },
                  meta: {},
                },
              ]);

              editor.createShape({
                type: "image",
                x: pos.x,
                y: pos.y,
                props: {
                  assetId: assetId,
                  w,
                  h,
                },
              });

              // Refresh credits and generations list
              queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.USER_PROFILE],
              });
              queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.USER_GENERATIONS],
              });
            };
            img.src = dataUrl;
          },
          onError: (error) => {
            activeGenerationsRef.current.delete(taskId);
            bumpGenerations();
            console.error(`Generation ${taskId} failed:`, error);

            // Delete placeholder
            editor.deleteShape(placeholderShapeId);

            alert("Generation failed: " + error.message);

            // Credits already refunded server-side, refresh to show updated count
            queryClient.invalidateQueries({
              queryKey: [QUERY_KEYS.USER_PROFILE],
            });
            queryClient.invalidateQueries({
              queryKey: [QUERY_KEYS.USER_GENERATIONS],
            });
          },
        });

        activeGenerationsRef.current.set(taskId, {
          placeholderShapeId,
          intervalId,
        });
        bumpGenerations();
      } catch (error) {
        console.error("Generation error:", error);
        alert("Failed to start generation: " + error.message);
      }
    },
    [editor, refreshUser, queryClient, bumpGenerations]
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <TopBar />
      <div style={{ flex: 1, position: "relative" }}>
        <Tldraw
          persistenceKey="scribbledraw-v2"
          components={{
            Scribble: LaserScribble,
          }}
          onMount={async (editor) => {
            setEditor(editor);
            editor.updateInstanceState({
              isDebugMode: false,
            });
            editor.setCurrentTool("draw");
            editor.setStyleForNextShapes(DefaultSizeStyle, "xl");
          }}
        />
        <GenerationLoadingOverlay
          editor={editor}
          activeGenerationsRef={activeGenerationsRef}
        />
        <WelcomeModal />
      </div>
      <GenerateBar onGenerate={handleGenerate} />
    </div>
  );
}
